/* eslint-disable import/no-extraneous-dependencies */
import React, {
  memo,
  useCallback,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
  useMemo
} from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import PropTypes from 'prop-types';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react'; // React Data Grid Component
import 'ag-grid-community/styles/ag-grid.css'; // Mandatory CSS required by the grid
import 'ag-grid-community/styles/ag-theme-quartz.css'; // Optional Theme applied to the grid

import { Text, GenericError, Alert, Icon, Button } from '../../../../../components';
import { useDragColumnChange } from '../../../hooks';
import { rowShape, columnDefsShape } from '../../../../../propsShape';

const PreviewLabel = styled(Text)`
  letter-spacing: -0.02em;
  background: var(--elevation-low);
  border-radius: 10px;
  height: 20px;
  box-sizing: border-box;
  align-content: center;
  border: 1px solid var(--border-muted);
  padding: 2px 8px;
  color: var(--content-subtitle);
  font-weight: 600;
  position: absolute;
  right: 12px;
  top: -12px;
  z-index: 1;
`;

const AnswerSourceContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  box-sizing: border-box;
`;

const AnswerTableContainer = styled.div`
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  border: none;

  --ag-font-size: 13px;
  --ag-font-family: var(--font-family-body);
  --ag-icon-font-color: var(--content-body);

  --ag-header-background-color: var(--wash-gray-default);
  --ag-header-foreground-color: var(--content-title);

  --ag-foreground-color: var(--content-body);
  --ag-background-color: var(--white);

  --ag-header-column-resize-handle-height: 100%;
  --ag-header-column-resize-handle-width: 1px;
  --ag-header-column-resize-handle-color: var(--border-muted);

  --ag-header-height: 40px;
  --ag-row-height: 40px;

  --ag-cell-horizontal-border: solid var(--border-muted);
  --ag-border-color: var(--border-muted);
  --ag-border-radius: 8px;
  --ag-row-border-color: var(--border-muted);

  --ag-row-hover-color: var(--elevation-mid);
  --ag-header-cell-hover-background-color: transparent;
  --ag-range-selection-border-color: var(--primary-default);

  .ag-header-cell {
    font-weight: 600;
  }

  & .ag-row.ag-row-last {
    --ag-row-border-color: transparent;
  }

  & .ag-cell.ag-column-last,
  & .ag-header-cell.ag-column-last {
    --ag-cell-horizontal-border: none;
  }

  & .ag-layout-auto-height {
    & .ag-center-cols-viewport,
    & .ag-center-cols-container {
      min-height: 40px;
    }
  }

  & .ag-overlay-no-rows-wrapper.ag-layout-auto-height {
    padding-top: 40px;
  }
`;

const ExpandButton = styled(Button)`
  position: absolute;
  bottom: 1px;
  left: 1px;
  right: 1px;
  height: 70px;
  width: calc(100% - 2px) !important;
  border-radius: 0px 0px 8px 8px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) -13.25%, var(--white) 32.4%);
  &:hover:not(:active) {
    background-color: transparent !important;
  }
  &:hover {
    background-color: transparent !important;
  }
`;

const AnswerTable = memo(
  forwardRef(
    (
      {
        rowData = [],
        columnDefs = [],
        pagination = false,
        pageSize = 10,
        preview = false,
        onColumnOrderChange = null,
        initialRowLimit = 7,
        shouldShowAllRows = false
      },
      forwardedRef
    ) => {
      const tableContainerRef = useRef(null);
      const apiRef = useRef(null);
      const gridComponentRef = useRef(null);
      const resolvedComponentRef = forwardedRef || gridComponentRef;
      const [apiReady, setApiReady] = useState(false);
      const [showAllRows, setShowAllRows] = useState(shouldShowAllRows);
      const { onDragStarted, onDragStopped } = useDragColumnChange(onColumnOrderChange);

      // Determine the data to display based on expand state
      const displayedRowData = useMemo(() => {
        if (showAllRows || rowData.length <= initialRowLimit) {
          return rowData;
        }
        return rowData.slice(0, initialRowLimit);
      }, [rowData, showAllRows, initialRowLimit]);

      const shouldShowExpandButton =
        pageSize > initialRowLimit && rowData.length > initialRowLimit && !showAllRows;

      const handleShowAllRows = useCallback(() => {
        setShowAllRows(true);
      }, []);

      // Expose the API through the forwarded ref when it becomes available
      useImperativeHandle(
        forwardedRef,
        () => ({
          api: apiRef.current
        }),
        [apiReady]
      );

      const adjustColumns = useCallback(() => {
        const gridApi = apiRef.current;
        const containerWidth = tableContainerRef.current?.clientWidth;
        if (!gridApi || !containerWidth) return;

        // Use a single requestAnimationFrame to avoid multiple layout thrashing
        requestAnimationFrame(() => {
          // Auto-size all columns first
          gridApi.autoSizeAllColumns();

          // Get columns after auto-sizing
          const columns = gridApi.getColumns();
          if (!columns || columns.length === 0) return;

          // Calculate total width needed
          const autoWidths = columns.map((col) => col.getActualWidth());
          const totalAutoWidth = autoWidths.reduce((sum, w) => sum + w, 0);

          // If columns fit within container, stretch to fill
          if (totalAutoWidth <= containerWidth) {
            gridApi.sizeColumnsToFit();
          } else {
            // Otherwise, cap overly wide columns and let others auto-size
            let adjustmentNeeded = false;
            columns.forEach((col, idx) => {
              if (autoWidths[idx] > 250) {
                gridApi.setColumnWidth(col, 250);
                adjustmentNeeded = true;
              }
            });

            // If we made adjustments, check again if we can fit to container
            if (adjustmentNeeded) {
              // Recalculate after capping columns
              const newAutoWidths = columns.map((col) => col.getActualWidth());
              const newTotalWidth = newAutoWidths.reduce((sum, w) => sum + w, 0);
              if (newTotalWidth <= containerWidth) {
                gridApi.sizeColumnsToFit();
              }
            }
          }
        });
      }, []);

      // Once AG Grid is ready, store the API and adjust columns
      const onGridReady = useCallback(
        (params) => {
          apiRef.current = params.api;
          gridComponentRef.current = params.api;
          setApiReady(true);
          adjustColumns();
        },
        [adjustColumns]
      );

      // Re‐adjust whenever the container resizes
      const onGridSizeChanged = useCallback(() => {
        adjustColumns();
      }, [adjustColumns]);

      // Re‐adjust whenever the data changes, but debounce it
      useEffect(() => {
        if (apiRef.current && displayedRowData.length > 0 && columnDefs.length > 0) {
          const timeoutId = setTimeout(adjustColumns, 150);
          return () => clearTimeout(timeoutId);
        }
        return undefined;
      }, [displayedRowData.length, columnDefs.length, adjustColumns]);

      const handleKeyDown = useCallback((event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === 'c') {
          navigator.clipboard.writeText(event.target.textContent);
        }
      }, []);

      useEffect(() => {
        const container = tableContainerRef.current;
        container?.addEventListener('keydown', handleKeyDown);
        return () => {
          container?.removeEventListener('keydown', handleKeyDown);
        };
      }, [handleKeyDown]);

      const renderNoRowsOverlay = () => <Text>No results</Text>;

      return (
        <ErrorBoundary fallback={<GenericError />}>
          {rowData.length === 5000 && (
            <Alert
              icon={<Icon name='alert' />}
              message='This answer exceeds your 5,000 row query limit. Please filter the results or reach out to Supper to access the full results.'
            />
          )}
          <AnswerSourceContainer>
            {preview && (
              <PreviewLabel as='label' $small>
                Preview
              </PreviewLabel>
            )}
            <AnswerTableContainer className='ag-theme-quartz' ref={tableContainerRef}>
              <AgGridReact
                ref={resolvedComponentRef}
                columnDefs={columnDefs}
                rowData={displayedRowData}
                domLayout='autoHeight'
                noRowsOverlayComponent={renderNoRowsOverlay}
                onGridReady={onGridReady}
                onGridSizeChanged={onGridSizeChanged}
                enableCellTextSelection
                pagination={pagination}
                paginationPageSize={pageSize}
                paginationPageSizeSelector={false}
                suppressMovableColumns={!onColumnOrderChange}
                onDragStarted={onDragStarted}
                onDragStopped={onDragStopped}
                autoSizePadding={0}
              />
              {shouldShowExpandButton && (
                <ExpandButton onClick={handleShowAllRows} naked labelStyle={{ fontSize: '11px' }}>
                  Expand the table
                  <Icon name='chevron-down' displaySize={16} />
                </ExpandButton>
              )}
            </AnswerTableContainer>
          </AnswerSourceContainer>
        </ErrorBoundary>
      );
    }
  )
);

AnswerTable.propTypes = {
  rowData: PropTypes.arrayOf(rowShape),
  columnDefs: columnDefsShape,
  pagination: PropTypes.bool,
  onColumnOrderChange: PropTypes.func,
  preview: PropTypes.bool,
  pageSize: PropTypes.number,
  initialRowLimit: PropTypes.number,
  shouldShowAllRows: PropTypes.bool
};

export { AnswerTable };
